// src/components/Header/NavBar.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  FiShoppingCart,
  FiUser,
  FiUserX,
  FiGitCommit,
  FiMinus,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import './Header.css';

const Chevron = ({ open }) =>
  open ? <FiChevronUp className="chev" /> : <FiChevronDown className="chev" />;

/**
 * Props:
 * - mode: 'desktop' | 'compact'
 * - drawerOpen: boolean
 * - closeDrawer: () => void
 */
const NavBar = ({ mode = 'desktop', drawerOpen = false, closeDrawer = () => {} }) => {
  const history = useHistory();

  // User (compat localStorage + Firebase)
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
        const compactUser = { email: u.email, uid: u.uid, name: u.displayName || '' };
        setUser(compactUser);
        localStorage.setItem('user', JSON.stringify(compactUser));
        localStorage.setItem('isLoggedIn', 'true');
      } else {
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('isLoggedIn');
      }
    });
    return () => unsub();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedIn');
      setUser(null);
      history.push('/');
      closeDrawer();
    } catch (e) {
      console.error('Error al cerrar sesión:', e);
    }
  }, [history, closeDrawer]);

  // ======== ESTADO PARA MOBILE (drawer) ========
  const [openL1, setOpenL1] = useState(null);           // 'galeria' | 'shop' | 'servicios' | null
  const [openGaleria, setOpenGaleria] = useState(null); // 'almas' | 'urbanos' | 'natural' | null

  const toggleL1 = (key) => {
    setOpenL1((curr) => (curr === key ? null : key));
    if (key !== 'galeria') setOpenGaleria(null);
  };
  const toggleGaleria = (key) => setOpenGaleria((curr) => (curr === key ? null : key));

  // ======== DESKTOP ========
  if (mode === 'desktop') {
    return (
      <nav className="navbar">
        <ul className="nav-menu">
          <li className="nav-item dropdown">
            <Link to="/galeria">Galería</Link>
            <ul className="dropdown-menu">
              <li><Link to="/galeria/abstractas">Abstractas</Link></li>

              <li className="nested-dropdown">
                <Link to="/galeria/almas-visibles">Almas Visibles</Link>
                <ul className="dropdown-submenu">
                  <li><Link to="/galeria/almas-visibles/retratos">Retratos</Link></li>
                  <li><Link to="/galeria/almas-visibles/en-movimiento">En Movimiento</Link></li>
                  <li><Link to="/galeria/almas-visibles/historias-anonimas">Historias Anónimas</Link></li>
                </ul>
              </li>

              <li className="nested-dropdown">
                <Link to="/galeria/paisajes-urbanos">Paisajes Urbanos</Link>
                <ul className="dropdown-submenu-solo">
                  <li><Link to="/galeria/paisajes-urbanos/arquitectura">Arquitectura</Link></li>
                  <li><Link to="/galeria/paisajes-urbanos/urbanismo">Urbanismo</Link></li>
                </ul>
              </li>

              <li className="nested-dropdown">
                <Link to="/galeria/entorno-natural">Entorno Natural</Link>
                <ul className="dropdown-submenu">
                  <li><Link to="/galeria/entorno-natural/paisajes">Paisajes</Link></li>
                  <li><Link to="/galeria/entorno-natural/naturaleza">Naturaleza</Link></li>
                  <li><Link to="/galeria/entorno-natural/fauna">Fauna</Link></li>
                </ul>
              </li>
            </ul>
          </li>

          <li className="nav-item dropdown">
            <Link to="/shop">Shop</Link>
            <ul className="dropdown-menu">
              <li><Link to="/shop/libros">Libros</Link></li>
              <li><Link to="/shop/obras">Obras</Link></li>
            </ul>
          </li>

          <li className="nav-item dropdown">
            <Link to="/servicios">Servicios</Link>
            <ul className="dropdown-menu">
              <li><Link to="/servicios/deporte">Deporte</Link></li>
              <li><Link to="/servicios/books-personalizados">Books Personalizados</Link></li>
              <li><Link to="/servicios/vinilos">Vinilos para paredes</Link></li>
              <li><Link to="/servicios/clases">Clases de Fotografía</Link></li>
            </ul>
          </li>

          <li className="nav-item"><Link to="/favoritos">Favoritos</Link></li>
          <li className="nav-item"><Link to="/sobre-mi">Sobre Mí</Link></li>
          <li className="nav-item"><Link to="/contacto">Contacto</Link></li>

          <li className="icon-item">
            <Link to="/carrito" className="icon-link" aria-label="Carrito">
              <FiShoppingCart size={20} />
            </Link>
          </li>

          <li className="icon-item">
            {user ? (
              <button
                onClick={handleLogout}
                className="icon-link user-icon"
                type="button"
                aria-label="Cerrar sesión"
                title="Cerrar sesión"
              >
                <FiUserX size={18} />
              </button>
            ) : (
              <Link to="/login" className="icon-link user-icon" aria-label="Iniciar sesión" title="Iniciar sesión">
                <FiUser size={18} />
              </Link>
            )}
          </li>
        </ul>
      </nav>
    );
  }

  // ======== COMPACT (drawer con acordeón controlado) ========
  return (
    <>
      <div
        className={`drawer-overlay ${drawerOpen ? 'open' : ''}`}
        onClick={closeDrawer}
        aria-hidden={!drawerOpen}
      />
      <nav className={`drawer ${drawerOpen ? 'open' : ''}`} id="mobile-drawer" aria-hidden={!drawerOpen}>
        <ul className="drawer-menu">
          {/* ========== Galería ========== */}
          <li className="drawer-item">
            <div className="row">
              <Link className="drawer-link" to="/galeria" onClick={closeDrawer}>Galería</Link>
              <button
                type="button"
                className="toggle-btn"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleL1('galeria'); }}
                aria-expanded={openL1 === 'galeria'}
                aria-controls="galeria-sub"
                aria-label="Abrir/cerrar Galería"
              >
                <Chevron open={openL1 === 'galeria'} />
              </button>
            </div>

            <ul id="galeria-sub" className={`drawer-submenu controlled ${openL1 === 'galeria' ? 'open' : ''}`}>
              <li className="l1">
                <span className="left-with-bullet">
                  <FiGitCommit className="bullet bullet-l1" />
                  <Link to="/galeria/abstractas" onClick={closeDrawer}>Abstractas</Link>
                </span>
              </li>

              <li className="l1 has-children">
                <div className="row">
                  <span className="left-with-bullet">
                    <FiGitCommit className="bullet bullet-l1" />
                    <Link to="/galeria/almas-visibles" onClick={closeDrawer}>Almas Visibles</Link>
                  </span>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGaleria('almas'); }}
                    aria-expanded={openGaleria === 'almas'}
                    aria-controls="galeria-almas"
                    aria-label="Abrir/cerrar Almas Visibles"
                  >
                    <Chevron open={openGaleria === 'almas'} />
                  </button>
                </div>
                <ul id="galeria-almas" className={`drawer-submenu controlled ${openGaleria === 'almas' ? 'open' : ''}`}>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/almas-visibles/retratos" onClick={closeDrawer}>Retratos</Link>
                    </span>
                  </li>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/almas-visibles/en-movimiento" onClick={closeDrawer}>En Movimiento</Link>
                    </span>
                  </li>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/almas-visibles/historias-anonimas" onClick={closeDrawer}>Historias Anónimas</Link>
                    </span>
                  </li>
                </ul>
              </li>

              <li className="l1 has-children">
                <div className="row">
                  <span className="left-with-bullet">
                    <FiGitCommit className="bullet bullet-l1" />
                    <Link to="/galeria/paisajes-urbanos" onClick={closeDrawer}>Paisajes Urbanos</Link>
                  </span>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGaleria('urbanos'); }}
                    aria-expanded={openGaleria === 'urbanos'}
                    aria-controls="galeria-urbanos"
                    aria-label="Abrir/cerrar Paisajes Urbanos"
                  >
                    <Chevron open={openGaleria === 'urbanos'} />
                  </button>
                </div>
                <ul id="galeria-urbanos" className={`drawer-submenu controlled ${openGaleria === 'urbanos' ? 'open' : ''}`}>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/paisajes-urbanos/arquitectura" onClick={closeDrawer}>Arquitectura</Link>
                    </span>
                  </li>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/paisajes-urbanos/urbanismo" onClick={closeDrawer}>Urbanismo</Link>
                    </span>
                  </li>
                </ul>
              </li>

              <li className="l1 has-children">
                <div className="row">
                  <span className="left-with-bullet">
                    <FiGitCommit className="bullet bullet-l1" />
                    <Link to="/galeria/entorno-natural" onClick={closeDrawer}>Entorno Natural</Link>
                  </span>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGaleria('natural'); }}
                    aria-expanded={openGaleria === 'natural'}
                    aria-controls="galeria-natural"
                    aria-label="Abrir/cerrar Entorno Natural"
                  >
                    <Chevron open={openGaleria === 'natural'} />
                  </button>
                </div>
                <ul id="galeria-natural" className={`drawer-submenu controlled ${openGaleria === 'natural' ? 'open' : ''}`}>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/entorno-natural/paisajes" onClick={closeDrawer}>Paisajes</Link>
                    </span>
                  </li>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/entorno-natural/naturaleza" onClick={closeDrawer}>Naturaleza</Link>
                    </span>
                  </li>
                  <li className="l2">
                    <span className="left-with-bullet">
                      <FiMinus className="bullet bullet-l2" />
                      <Link to="/galeria/entorno-natural/fauna" onClick={closeDrawer}>Fauna</Link>
                    </span>
                  </li>
                </ul>
              </li>
            </ul>
          </li>

          {/* ========== Shop ========== */}
          <li className="drawer-item">
            <div className="row">
              <Link className="drawer-link" to="/shop" onClick={closeDrawer}>Shop</Link>
              <button
                type="button"
                className="toggle-btn"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleL1('shop'); }}
                aria-expanded={openL1 === 'shop'}
                aria-controls="shop-sub"
                aria-label="Abrir/cerrar Shop"
              >
                <Chevron open={openL1 === 'shop'} />
              </button>
            </div>
            <ul id="shop-sub" className={`drawer-submenu controlled ${openL1 === 'shop' ? 'open' : ''}`}>
              <li className="l1">
                <span className="left-with-bullet">
                  <FiGitCommit className="bullet bullet-l1" />
                  <Link to="/shop/libros" onClick={closeDrawer}>Libros</Link>
                </span>
              </li>
              <li className="l1">
                <span className="left-with-bullet">
                  <FiGitCommit className="bullet bullet-l1" />
                  <Link to="/shop/obras" onClick={closeDrawer}>Obras</Link>
                </span>
              </li>
            </ul>
          </li>

          {/* ========== Servicios ========== */}
          <li className="drawer-item">
            <div className="row">
              <Link className="drawer-link" to="/servicios" onClick={closeDrawer}>Servicios</Link>
              <button
                type="button"
                className="toggle-btn"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleL1('servicios'); }}
                aria-expanded={openL1 === 'servicios'}
                aria-controls="serv-sub"
                aria-label="Abrir/cerrar Servicios"
              >
                <Chevron open={openL1 === 'servicios'} />
              </button>
            </div>
            <ul id="serv-sub" className={`drawer-submenu controlled ${openL1 === 'servicios' ? 'open' : ''}`}>
              {[
                {to:'/servicios/deporte', label:'Deporte'},
                {to:'/servicios/books-personalizados', label:'Books Personalizados'},
                {to:'/servicios/vinilos', label:'Vinilos para paredes'},
                {to:'/servicios/clases', label:'Clases de Fotografía'},
              ].map(({to,label})=>(
                <li key={to} className="l1">
                  <span className="left-with-bullet">
                    <FiGitCommit className="bullet bullet-l1" />
                    <Link to={to} onClick={closeDrawer}>{label}</Link>
                  </span>
                </li>
              ))}
            </ul>
          </li>

          {/* Resto */}
          <li className="drawer-item"><Link className="drawer-link" to="/favoritos" onClick={closeDrawer}>Favoritos</Link></li>
          <li className="drawer-item"><Link className="drawer-link" to="/sobre-mi" onClick={closeDrawer}>Sobre Mí</Link></li>
          <li className="drawer-item"><Link className="drawer-link" to="/contacto" onClick={closeDrawer}>Contacto</Link></li>

          {/* Iconos inferiores */}
          <li className="drawer-item icons-row">
            <Link to="/carrito" className="icon-link" onClick={closeDrawer}><FiShoppingCart size={20} /></Link>
            {user ? (
              <button className="icon-link user-icon" onClick={handleLogout} type="button" aria-label="Cerrar sesión" title="Cerrar sesión">
                <FiUserX size={18} />
              </button>
            ) : (
              <Link to="/login" className="icon-link user-icon" onClick={closeDrawer} aria-label="Iniciar sesión" title="Iniciar sesión">
                <FiUser size={18} />
              </Link>
            )}
          </li>
        </ul>
      </nav>
    </>
  );
};

export default NavBar;
